import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { getAdminVacanciesAnalyticsApi, AdminVacancyItem } from "../../features/analytics/api/adminAnalytics.api"
import { GroupedBarChart, DonutChart, ChartCard } from "./Charts"
import { ChevronRightIcon, ExclamationTriangleIcon, BriefcaseIcon } from "@heroicons/react/16/solid"

const LIMIT = 5

const StatusBadge = ({ active }: { active: boolean }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
    active ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" : "bg-gray-100 text-gray-500"
  }`}>
    <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-emerald-500" : "bg-gray-400"}`} />
    {active ? "Активна" : "Закрыта"}
  </span>
)

export const VacanciesAnalyticsPage = () => {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [items, setItems] = useState<AdminVacancyItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const load = () => {
    setLoading(true)
    setError(false)
    getAdminVacanciesAnalyticsApi()
      .then((res) => {
        setItems(res.data.items)
        setTotal(res.data.total)
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  if (error) {
    return (
      <div className="bg-white border border-gray-100 rounded-2xl py-16 text-center">
        <ExclamationTriangleIcon className="w-8 h-8 text-red-200 mx-auto mb-2" />
        <p className="text-sm text-gray-400 mb-2">Не удалось загрузить вакансии</p>
        <button onClick={load} className="text-xs font-medium text-blue-600 hover:text-blue-700 underline">
          Повторить
        </button>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="space-y-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => <div key={i} className="bg-white border border-gray-100 rounded-2xl h-16 animate-pulse" />)}
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 h-64 animate-pulse" />
      </div>
    )
  }

  const totalPages = Math.max(1, Math.ceil(items.length / LIMIT))
  const pageItems = items.slice((page - 1) * LIMIT, page * LIMIT)

  const activeCount  = items.filter((v) => v.is_active).length
  const closedCount  = items.filter((v) => !v.is_active).length
  const totalViews   = items.reduce((s, v) => s + v.views, 0)
  const totalApps    = items.reduce((s, v) => s + v.applications, 0)

  return (
    <div className="space-y-5">
      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Всего вакансий", value: total,        color: "text-gray-800" },
          { label: "Просмотров",     value: totalViews,   color: "text-blue-600" },
          { label: "Заявок",         value: totalApps,    color: "text-emerald-600" },
          { label: "Активных",       value: activeCount,  color: "text-violet-600" },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white border border-gray-100 rounded-2xl px-4 py-3">
            <p className="text-xs text-gray-400 mb-0.5">{label}</p>
            <p className={`text-xl font-bold ${color}`}>{value.toLocaleString("ru")}</p>
          </div>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-2xl py-16 text-center">
          <BriefcaseIcon className="w-8 h-8 text-gray-200 mx-auto mb-2" />
          <p className="text-sm text-gray-400">Вакансий пока нет</p>
        </div>
      ) : (
        <>
          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <ChartCard title="Просмотры и заявки по вакансиям">
              <GroupedBarChart
                data={items.map((v) => ({
                  label: v.title.split(" ").slice(0, 2).join(" "),
                  a: v.views,
                  b: v.applications,
                }))}
                labelA="Просмотры"
                labelB="Заявки"
                colorA="#3b82f6"
                colorB="#10b981"
              />
            </ChartCard>
            <ChartCard title="Статус вакансий">
              <DonutChart data={[
                { label: "Активные",  value: activeCount, color: "#10b981" },
                { label: "Закрытые", value: closedCount,  color: "#e5e7eb" },
              ]} />
            </ChartCard>
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b bg-gray-50/60 flex items-center justify-between">
              <h3 className="font-semibold text-gray-800">Все вакансии</h3>
              <span className="text-xs text-gray-400">{total} вакансий</span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50/40">
                    {["Вакансия", "Просмотры", "Заявки", "Конверсия", "Статус", "Создана", ""].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {pageItems.map((v) => (
                    <tr key={v.id}
                      onClick={() => navigate(`/admin/analytics/vacancies/${v.id}`)}
                      className="hover:bg-blue-50/40 transition-colors cursor-pointer group">
                      <td className="px-4 py-3.5">
                        <p className="font-medium text-gray-800">{v.title}</p>
                      </td>
                      <td className="px-4 py-3.5 text-gray-700">{v.views.toLocaleString("ru")}</td>
                      <td className="px-4 py-3.5 text-gray-700">{v.applications}</td>
                      <td className="px-4 py-3.5">
                        <span className={`font-semibold ${v.application_rate >= 6 ? "text-emerald-600" : v.application_rate >= 4 ? "text-amber-600" : "text-red-500"}`}>
                          {v.application_rate}%
                        </span>
                      </td>
                      <td className="px-4 py-3.5"><StatusBadge active={v.is_active} /></td>
                      <td className="px-4 py-3.5 text-gray-500 text-xs">{v.created_at.slice(0, 10)}</td>
                      <td className="px-4 py-3.5">
                        <ChevronRightIcon className="w-4 h-4 text-gray-300 group-hover:text-blue-500 transition-colors" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="px-5 py-3.5 border-t bg-gray-50/40 flex items-center justify-between">
              <span className="text-xs text-gray-400">
                Стр. <span className="font-medium text-gray-600">{page}</span> из{" "}
                <span className="font-medium text-gray-600">{totalPages}</span>
              </span>
              <div className="flex gap-1.5">
                <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}
                  className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  ← Пред.
                </button>
                <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
                  className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  След. →
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
